import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Textarea } from 'components/ui/textarea';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

type JsonSchemaEditorModalProps = {
  open: boolean;
  initialValue: string;
  onOpenChange: (open: boolean) => void;
  onSave: (value: string | null) => void;
};

function getValidationError(value: string, t: TFunction) {
  if (!value.trim()) {
    return t('please_provide_valid_json_schema_with_at_least_one_field');
  }

  try {
    const parsed = JSON.parse(value);

    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Object.keys(parsed).length > 0
    ) {
      return '';
    }

    return t('PLEASE_PROVIDE_VALID_JSON_SCHEMA_WITH_AT_LEAST_ONE_FIELD');
  } catch (error) {
    return error instanceof Error ? error.message : t('INVALID_JSON_SCHEMA');
  }
}

export function JsonSchemaEditorModal({
  open,
  initialValue,
  onOpenChange,
  onSave,
}: JsonSchemaEditorModalProps) {
  const [jsonValue, setJsonValue] = useState(initialValue);
  const { t } = useTranslation();
  useEffect(() => {
    if (open) {
      setJsonValue(initialValue);
    }
  }, [initialValue, open]);

  const validationError = useMemo(() => getValidationError(jsonValue, t), [jsonValue, t]);
  const isValid = !validationError;

  const handleSave = () => {
    if (!isValid) {
      return;
    }

    onSave(jsonValue);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('JSON_RESPONSE_SCHEMA')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 overflow-hidden">
          <div className="overflow-hidden rounded-lg border border-border bg-background">
            <Textarea
              value={jsonValue}
              onChange={(event) => setJsonValue(event.target.value)}
              className="min-h-[360px] resize-none border-0 p-4 font-mono text-sm leading-6 shadow-none focus-visible:ring-0"
              spellCheck={false}
            />
          </div>

          {validationError ? (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{validationError}</p>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{t('VALID_JSON_SCHEMA')}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('cancel')}
          </Button>
          <Button type="button" onClick={handleSave} disabled={!isValid}>
            {t('save_schema')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
